/**
 * jobs + job_statuses + job_types + tenant_job_type_settings (DB-03).
 *
 * The central transactional schema for the entire platform. Phases 5/6/7/9/10/
 * 11/12 all read from or write to `jobs` — its shape is load-bearing.
 *
 * Design split (CONTEXT D-05/D-06/D-07/D-08, D-16):
 *   - `job_statuses` is a 16-row LOOKUP table with NO tenant_id and NO RLS.
 *     `jobs.status_id` FK references it. Phase 5 reads `is_terminal` +
 *     `sort_order` for state-machine metadata; the actual transition
 *     allowlist + validator code lives in `@lodex-pro/api` (D-07).
 *   - `job_types` is a PLATFORM-MANAGED catalog (no tenant_id, no RLS) per
 *     D-06. Per-tenant overrides live in `tenant_job_type_settings`.
 *     master spec §12 BILLING METHODS column shape: billing_method +
 *     default_rate_cents + unit_of_measure_code. DEC-003 adds seal_required.
 *     DEC-014 / master spec §89 adds capture_type discriminator (default 'HAUL';
 *     non-HAUL types unlock equipment-delivery / equipment-pickup / service-call
 *     workflows in Phase 24+).
 *   - `tenant_job_type_settings` is the tenant-scoped OVERLAY (D-06): RLS
 *     enforced; per-tenant enable/disable + custom rate + custom label.
 *   - `jobs` itself is tenant-scoped with RLS and the DEC-001 three-field
 *     DispatcherId split (created_by_id + created_by_type + assigned_dispatcher_id).
 *
 * RLS posture:
 *   - job_statuses + job_types: NO pgPolicy (D-16 platform-managed catalogs).
 *     Read access via the GRANT in Plan 02-05's epilogue; write access REVOKEd
 *     from app_user (only `migrator` mutates).
 *   - tenant_job_type_settings: RLS via standard
 *     `tenant_id = current_setting('app.current_tenant_id')::uuid` predicate.
 *   - jobs: RLS — same predicate.
 *
 * Cross-plan FK wiring (Plan 02-04 finalized):
 *   - `jobs.site_id` → `company_sites.id` (cross-plan FK to Plan 02-03 sites.ts)
 *   - `jobs.operator_id` → `operators.id` (cross-plan FK to Plan 02-03 sites.ts)
 *   Both are declared inline via `.references()` here — Plan 02-04 modified
 *   this file to add the FK constraints once Plan 02-03's sites.ts existed.
 *   Drizzle 0.45.x's relations() API is for query-builder joins, not for
 *   declarative FK constraints across files; the inline `.references()`
 *   approach (with `import { companySites, operators } from './sites.js'`)
 *   is the cleanest pattern. No circular import risk — sites.ts does NOT
 *   import jobs.ts.
 *
 * Sources:
 *   - master spec §8 (DEFINITIVE 11-STATE MODEL — 16 status codes; status_id semantics)
 *   - master spec §9 (DISPATCHER ID SPLIT — DEC-001 three-field design)
 *   - master spec §12 (BILLING METHODS — job_types billing_method/rate_cents/unit_of_measure)
 *   - master spec §14 (HAUL ID — `{abbrev}{9 digit sequence}` per-customer
 *     format; widened from legacy 9-digit-only via Phase 6 migration 0005)
 *   - master spec §89 (capture_type discriminator — DEC-014)
 *   - CONTEXT D-05, D-06, D-07, D-08 (job-status + canonical-type storage)
 *   - CONTEXT D-16 (platform-catalog NO RLS)
 *   - DEC-001 (three-field DispatcherId split)
 *   - DEC-003 (seal_required boolean)
 *   - DEC-014 (capture_type column)
 *   - RESEARCH Performance Indexes #6-11 (jobs) and #31 (tenant_job_type_settings)
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - jobs.haul_id is varchar(20) via CHECK regex `^[A-Z]{2,6}[0-9]{9}$`
 *     (master spec §14 + Phase 6 / Plan 06-01 / D-22 step 3; widened from
 *     legacy varchar(9) digits-only via migration 0005).
 *   - jobs.created_by_type allowlist is the 5 DEC-001 values:
 *       DISPATCHER, DRIVER, FOREMAN, SYSTEM_AUTO_HAUL, SYSTEM_MQTT
 *   - job_types.billing_method allowlist is the 3 §12 values: PER_BBL, HOURLY, PER_LOAD
 *   - job_types.capture_type allowlist is the 4 §89 values:
 *       HAUL, EQUIPMENT_DELIVERY, EQUIPMENT_PICKUP, SERVICE_CALL (default 'HAUL')
 *   - job_types.require_water_volume / require_oil_volume / require_gauge_readings
 *     are boolean NOT NULL DEFAULT false. They affect SINGLE_GAUGE capture
 *     required-ness only; ignored for BEFORE_AFTER (DEC-025).
 *   - job_types.completion_location allowlist is the 3 §13 / DEC-029 values:
 *     DISPOSAL_FACILITY, ON_SITE, OTHER (default 'DISPOSAL_FACILITY').
 *   - job_types.require_signature_reason is boolean NOT NULL DEFAULT false.
 *     Drives the mobile SignatureCaptureScreen "No signature available —
 *     record reason" button visibility (quick task 260517-azl issue #2).
 *   - job_types.default_product_id is a nullable FK to products(id) with
 *     ON DELETE SET NULL. Populated for the demo hauler tenant's
 *     PRODUCED_WATER_HAUL by the demo orchestrator seed (DEC-026); future
 *     job_types may leave it NULL or wire it to a tenant-scoped product row.
 *   - jobs.{planned,actual}_disposal_facility_id are FK to disposal_facilities.id
 *     ON DELETE RESTRICT (DEC-027 #8 / D-07 / D-09). planned set at job
 *     creation; actual set when driver taps Arrived at Disposal (defaults to
 *     planned; driver may override via DisposalScreen "Change destination").
 *   - jobs.reference_id is nullable TEXT — driver-entered PO / reference /
 *     order-number captured on the run ticket. Required only when the parent
 *     operator has `require_reference_id = true`; gate enforced by
 *     `POST /v1/jobs/:id/capture` (Plan 06.2-16). Length bounded at API Zod
 *     layer (max 64 chars) — NOT at DB. BILLCFG-04 / DEC-032 #5.
 */

import {
  boolean,
  check,
  foreignKey,
  index,
  integer,
  jsonb,
  pgPolicy,
  pgTable,
  primaryKey,
  serial,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { operators } from './sites.js';
import { products } from './products.js';
// Phase 06.1 / DEC-027 #8 — disposal-facility planned/actual override model.
import { disposalFacilities } from './disposals.js';
import {
  haulerDisposalFacilities,
  haulerOperators,
  haulerLocations,
  wells,
} from './platform-catalog.js';
// OPEN-053 — jobs.requestor_id. Safe direction: requestors.ts imports platform-catalog and
// users, never jobs, so there is no cycle to work around here.
import { requestors } from './requestors.js';
import { importBatches } from './imports.js';

/**
 * job_statuses — 16-row LOOKUP table per CONTEXT D-05.
 *
 * NO tenant_id, NO RLS (D-16 platform-managed catalog). Plan 02-06 seeds
 * exactly 16 rows by iterating JOB_STATUS_CODES from enums.ts. The boot-time
 * assertion in Plan 02-07 reads JOB_STATUS_CODES.length and refuses to start
 * the API if the row count diverges.
 *
 * `id` is `serial` (auto-increment int) so jobs.status_id is a small foreign
 * key. JOIN cost is negligible (16 rows, indexed on `id` + `code`). The
 * `code` column matches the JOB_STATUS_CODES literal-string union (D-08).
 * Phase 5's state-machine validator reads `is_terminal` + `sort_order` for
 * transition metadata — actual transition allowlist code is in @lodex-pro/api.
 */
export const jobStatuses = pgTable(
  'job_statuses',
  {
    id: serial('id').primaryKey(),
    // Matches JOB_STATUS_CODES literal — UNIQUE'd below.
    code: text('code').notNull(),
    // Human-readable label. The seed is insert-only (OPEN-167: ON CONFLICT (code) DO NOTHING,
    // run on every deploy), so a re-run never changes it; a label change needs a migration.
    label: text('label').notNull(),
    // Matches JOB_STATUS_CODES array index — Plan 02-06 seed pins this.
    sortOrder: integer('sort_order').notNull(),
    // CANCELLED + REJECTED are terminal; the other 14 are not. Phase 5 reads.
    isTerminal: boolean('is_terminal').notNull().default(false),
  },
  (t) => [
    uniqueIndex('job_statuses_code_idx').on(t.code),
    index('job_statuses_sort_order_idx').on(t.sortOrder),
    // No pgPolicy — D-16 platform-managed catalog. Read access via the GRANT
    // in Plan 02-05's epilogue; write access REVOKEd from app_user.
  ],
);

export type JobStatus = typeof jobStatuses.$inferSelect;
export type NewJobStatus = typeof jobStatuses.$inferInsert;
export const jobStatusSelectSchema = createSelectSchema(jobStatuses);
export const jobStatusInsertSchema = createInsertSchema(jobStatuses);

/**
 * job_types — TENANT-OWNED catalog (DEC-140, reverses CONTEXT D-06).
 *
 * tenant_id + RLS + per-tenant writes (DISPATCH_MANAGER+, MANAGE_REFERENCE_DATA).
 * Each hauler builds its own set; NEW tenants start with ZERO (no auto-seed). The
 * old dormant `tenant_job_type_settings` overlay was folded in + dropped: its
 * custom_rate_cents → default_rate_cents, custom_label → label, enabled →
 * is_active, is_default_for_dispatch → the column of the same name here.
 *
 * billing_method allowlist (§12): PER_BBL | HOURLY | PER_LOAD.
 * capture_type allowlist (§89 / DEC-014): HAUL | EQUIPMENT_DELIVERY |
 *   EQUIPMENT_PICKUP | SERVICE_CALL (default 'HAUL' so existing seeds get
 *   HAUL implicitly without specifying the column).
 * seal_required (DEC-003) tracks whether the job_type requires the driver
 * to capture a seal number on close-out (used by Phase 6 capture form).
 * DEC-025 adds require_water_volume / require_oil_volume / require_gauge_readings
 * (tenant-configurable per-tank required-ness flags — SINGLE_GAUGE only;
 * BEFORE_AFTER ignores them).
 * DEC-026 adds default_product_id (FK to products.id, nullable, SET NULL on
 * delete — the per-tank product cascade source-of-truth).
 */
export const jobTypes = pgTable(
  'job_types',
  {
    id: serial('id').primaryKey(),
    // DEC-140 — job_types is now TENANT-OWNED (reverses D-06). Each hauler builds
    // its own set; new tenants start with zero. RLS-scoped; writes app-layer
    // (DISPATCH_MANAGER+). Migration 0055.
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // Stable machine code (e.g. 'PRODUCED_WATER_HAUL') — UNIQUE per tenant (below).
    code: text('code').notNull(),
    label: text('label').notNull(),
    // Master spec §12 — CHECK enforced below.
    billingMethod: text('billing_method').notNull(),
    // Platform-default rate; nullable when the type is metered (e.g. HOURLY may
    // delegate rate entirely to the tenant overlay).
    defaultRateCents: integer('default_rate_cents'),
    // Master spec §12 — e.g. 'BBL' | 'GAL' | 'HOUR'. Free-text in this phase
    // (no FK to a units table); a units catalog can land later if needed.
    unitOfMeasureCode: text('unit_of_measure_code'),
    // DEPRECATED (Phase 09.1 / JOBTYPE-05) — superseded by *_requirement enums.
    // Kept as an API-mirrored transition column; every writer MUST set both.
    // Dropped in a follow-up migration outside 09.1.
    // DEC-003 — seal capture required at job close-out.
    sealRequired: boolean('seal_required').notNull().default(false),
    // DEC-014 / master spec §89 — Phase 24 enables non-HAUL types.
    captureType: text('capture_type').notNull().default('HAUL'),
    // DEC-024 / master spec §13 — per-method capture shape discriminator.
    // SINGLE_GAUGE: top/bottom feet+inches captured raw + driver-entered
    //   water_volume_bbl + oil_volume_bbl per tank (default — produced-water).
    // BEFORE_AFTER: per-tank before_bbl + after_bbl (DEC-023 shape).
    captureMethod: text('capture_method').notNull().default('SINGLE_GAUGE'),
    // D-17 / Phase 06.1 — per-job_type completion-location discriminator.
    // DISPOSAL_FACILITY: driver completes at a registered disposal_facilities row
    //   (default — produced-water hauling). Triggers DisposalScreen + the
    //   ARRIVED_DISPOSAL → DISPOSAL_COMPLETE lifecycle.
    // ON_SITE: job completes at the pickup location. No DisposalScreen.
    //   Lifecycle short-circuits to COMPLETED at GAUGE_ENTRY_SAVED.
    // OTHER: driver enters a free-text destination at completion time. Lifecycle
    //   tail still fires (driver still travels + arrives somewhere) but
    //   actual_disposal_facility_id stays NULL — the manual destination is
    //   the audit record on haul_captures.other_destination_text.
    completionLocation: text('completion_location').notNull().default('DISPOSAL_FACILITY'),
    // DEPRECATED (Phase 09.1 / JOBTYPE-05) — superseded by *_requirement enums.
    // Kept as API-mirrored transition columns; every writer MUST set both.
    // Dropped in a follow-up migration outside 09.1.
    // DEC-025 / master spec §13 — tenant-configurable per-tank required fields
    // for SINGLE_GAUGE captures. No-ops for BEFORE_AFTER (which has before_bbl/
    // after_bbl always required by method shape per DEC-024). Defaults to false
    // — the platform ships permissive; tenants flip flags per job_type via the
    // future admin UI (out of scope for DEC-025). Single flag for the gauge
    // pair (top+bottom always travel together — see DEC-025 rationale).
    requireWaterVolume: boolean('require_water_volume').notNull().default(false),
    requireOilVolume: boolean('require_oil_volume').notNull().default(false),
    requireGaugeReadings: boolean('require_gauge_readings').notNull().default(false),
    // Issue #2 (2026-05-17, quick task 260517-azl) — tenant-configurable
    // per-job_type "driver must record a reason when no signature is
    // captured" flag. Default false — most job_types do not need a skip
    // reason. Read by mobile SignatureCaptureScreen to hide/show the
    // "No signature available — record reason" button. Same pattern as
    // the DEC-025 require_* flags above (column → API JobDetail envelope →
    // mobile screen prop). Unrelated to seal_required (DEC-003) which
    // captures seal NUMBERS, not signatures.
    requireSignatureReason: boolean('require_signature_reason').notNull().default(false),
    // DEC-026 (2026-05-17, quick 260517-d8h) — tenant-scoped product cascade.
    // Nullable so existing rows do not need backfill at migration time. The
    // demo orchestrator's products seed (demo/05-products.ts) wires the demo
    // hauler tenant's PRODUCED_WATER_HAUL.default_product_id to its seeded
    // PRODUCED_WATER product; in production this column stays NULL until the
    // tenant admin UI lands. ON DELETE SET NULL because the job_type default is
    // a configuration pointer, not an audit record — if a product is removed
    // (must be unreferenced by historical captures per the RESTRICT FK on
    // haul_capture_tanks.product_id), the default falling to NULL is the
    // correct recovery.
    defaultProductId: uuid('default_product_id').references(() => products.id, {
      onDelete: 'set null',
    }),
    // DEC-140 — folded from the dropped tenant_job_type_settings overlay:
    // "default selection in the dispatcher new-job form" affordance.
    isDefaultForDispatch: boolean('is_default_for_dispatch').notNull().default(false),
    // D-01 / JOBTYPE-01 — version rows. family_id is the stable identity ACROSS
    // versions; jobs.job_type_id stays an FK to a SPECIFIC row, so an in-flight
    // job is pinned to its version automatically (D-04 — no migration of
    // in-progress jobs, ever). superseded_at NULL = this is the live version.
    familyId: uuid('family_id').notNull().defaultRandom(),
    version: integer('version').notNull().default(1),
    supersededAt: timestamp('superseded_at', { withTimezone: true }),
    // D-03 / D-13 / JOBTYPE-03 — the fork discriminator. LINE_ITEM is REAL and
    // selectable, but 09.1 ships NO Line-Item capture path and NO Run/Stop/
    // LineItem tables (D-13). capture-write.ts rejects a capture POST against a
    // LINE_ITEM job type with LINE_ITEM_CAPTURE_NOT_SUPPORTED.
    ticketShape: text('ticket_shape').notNull().default('TANK'),
    // JOBTYPE-04 — tank count limit. SINGLE = exactly 1 tank; MAX_N = at most
    // tank_count_max (>= 2); UNLIMITED = no cap. NULL only when LINE_ITEM.
    tankCountMode: text('tank_count_mode').default('UNLIMITED'),
    tankCountMax: integer('tank_count_max'),
    // JOBTYPE-05 / JOBTYPE-06 — TANK-fork field requirements (HIDDEN|OPTIONAL|
    // REQUIRED). NULL exactly when ticket_shape = 'LINE_ITEM' (D-03 partial
    // CHECK below). DEC-170 splits DEC-025's single gauge flag into top+bottom;
    // job_types_gauge_coherence_check keeps the incoherent combination
    // (bottom stricter than top) unrepresentable rather than re-merging them.
    waterVolumeRequirement: text('water_volume_requirement').default('OPTIONAL'),
    oilVolumeRequirement: text('oil_volume_requirement').default('OPTIONAL'),
    topGaugeRequirement: text('top_gauge_requirement').default('OPTIONAL'),
    bottomGaugeRequirement: text('bottom_gauge_requirement').default('OPTIONAL'),
    sealRequirement: text('seal_requirement').default('OPTIONAL'),
    colorCutRequirement: text('color_cut_requirement').default('OPTIONAL'),
    // D-11 — governs only what the DRIVER is shown/required to type. The
    // positional slot identifier stays haul_capture_tanks.position (DEC-023 /
    // DEC-025 "tank number always required" is about position, NOT tank_name).
    // haul_capture_tanks.tank_name stays NOT NULL — the server always writes one
    // of: linked hauler_tanks label -> driver input -> 'TANK {position+1}'.
    tankNameRequirement: text('tank_name_requirement').default('OPTIONAL'),
    // DEC-176 (ClickUp 86bb0yq4b) — "require a product selection before completing
    // a tank capture", relevant ONLY when default_product_id IS NULL. Constrained to
    // OPTIONAL/REQUIRED (HIDDEN is meaningless for a per-tank product picker). Nullable
    // to match the sibling tank-fork enums (NULL under a LINE_ITEM job type). NON-
    // structural (edited in place, like the other *_requirement enums).
    productRequirement: text('product_requirement').default('OPTIONAL'),
    // JOBTYPE-07 — cross-fork (apply on BOTH forks), so NOT NULL always.
    // DEC-170's 2026-07-24 correction: driver comment is three-state, not boolean.
    driverCommentRequirement: text('driver_comment_requirement').notNull().default('OPTIONAL'),
    // driver_time_requirement is backfilled REQUIRED for billing_method='HOURLY'
    // so HOURLY-02's DRIVER_TOTAL_TIME_REQUIRED gate keeps behaving identically.
    driverTimeRequirement: text('driver_time_requirement').notNull().default('HIDDEN'),
    // OPEN-121 / migration 0242 — the load-photo rule. REQUIRED: the driver takes a photo or
    // skips it with a reason (haul_captures.photo_skip_reason), and In Review flags
    // MISSING_PHOTO. Cross-fork, NOT NULL; NON-structural (edited in place).
    photoRequirement: text('photo_requirement').notNull().default('OPTIONAL'),
    // D-08 / D-09 / JOBTYPE-09 — explicit intent flag. The COALESCE(operator
    // rate, job_type rate) fallback in emit-billable-events.ts is RETAINED
    // unchanged; the "requested but no customer rate set" case is SURFACED
    // (billable_events.notes + log.warn), never a hard error at capture.
    useCompanyPricing: boolean('use_company_pricing').notNull().default(false),
    // D-06 / JOBTYPE-10 — the BILLING half of "End Job With Disposal". The
    // LIFECYCLE half is the existing completion_location discriminator and is
    // NOT touched. Two knobs, deliberately.
    disposalFeeEnabled: boolean('disposal_fee_enabled').notNull().default(false),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // D-02 — code stays STABLE across versions; uniqueness moves to the LIVE
    // version. Every code-based lookup (sync receiver receive.ts, reference-data
    // .ts, the create-endpoint duplicate pre-check) MUST add
    // `superseded_at IS NULL` or it resolves ambiguously. See 09.1 Plans 02/03.
    uniqueIndex('job_types_tenant_code_live_idx')
      .on(t.tenantId, t.code)
      .where(sql`${t.supersededAt} IS NULL`),
    uniqueIndex('job_types_tenant_family_version_idx').on(t.tenantId, t.familyId, t.version),
    index('job_types_tenant_family_idx').on(t.tenantId, t.familyId),
    index('job_types_tenant_active_idx').on(t.tenantId, t.isActive),
    // Master spec §12 billing method allowlist.
    check(
      'job_types_billing_method_check',
      sql`billing_method IN ('PER_BBL', 'HOURLY', 'PER_LOAD', 'PER_UNIT')`,
    ),
    // DEC-014 / master spec §89 capture-type allowlist.
    check(
      'job_types_capture_type_check',
      sql`capture_type IN ('HAUL', 'EQUIPMENT_DELIVERY', 'EQUIPMENT_PICKUP', 'SERVICE_CALL')`,
    ),
    // DEC-024 / master spec §13 capture-method allowlist.
    check(
      'job_types_capture_method_check',
      sql`capture_method IN ('SINGLE_GAUGE', 'BEFORE_AFTER')`,
    ),
    // D-17 / Phase 06.1 completion-location allowlist.
    check(
      'job_types_completion_location_check',
      sql`completion_location IN ('DISPOSAL_FACILITY', 'ON_SITE', 'OTHER')`,
    ),
    // JOBTYPE-03 — ticket-shape fork discriminator.
    check('job_types_ticket_shape_check', sql`ticket_shape IN ('TANK', 'LINE_ITEM')`),
    // JOBTYPE-04 — tank count mode allowlist.
    check(
      'job_types_tank_count_mode_check',
      sql`tank_count_mode IS NULL OR tank_count_mode IN ('SINGLE', 'MAX_N', 'UNLIMITED')`,
    ),
    // JOBTYPE-04 — tank_count_max required (and >= 2) exactly when mode = MAX_N.
    check(
      'job_types_tank_count_max_check',
      sql`(tank_count_mode = 'MAX_N' AND tank_count_max IS NOT NULL AND tank_count_max >= 2)
          OR (tank_count_mode IS DISTINCT FROM 'MAX_N' AND tank_count_max IS NULL)`,
    ),
    // JOBTYPE-05/06 — Hidden/Optional/Required allowlists, one per tank-fork column.
    check(
      'job_types_water_volume_requirement_check',
      sql`water_volume_requirement IS NULL OR water_volume_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    check(
      'job_types_oil_volume_requirement_check',
      sql`oil_volume_requirement IS NULL OR oil_volume_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    check(
      'job_types_top_gauge_requirement_check',
      sql`top_gauge_requirement IS NULL OR top_gauge_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    check(
      'job_types_bottom_gauge_requirement_check',
      sql`bottom_gauge_requirement IS NULL OR bottom_gauge_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    check(
      'job_types_seal_requirement_check',
      sql`seal_requirement IS NULL OR seal_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    check(
      'job_types_color_cut_requirement_check',
      sql`color_cut_requirement IS NULL OR color_cut_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    check(
      'job_types_tank_name_requirement_check',
      sql`tank_name_requirement IS NULL OR tank_name_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    // DEC-176 — product_requirement is OPTIONAL/REQUIRED only (no HIDDEN); mirrors
    // the migration 0078 CHECK.
    check(
      'job_types_product_requirement_check',
      sql`product_requirement IS NULL OR product_requirement IN ('OPTIONAL', 'REQUIRED')`,
    ),
    // JOBTYPE-07 — cross-fork, NOT NULL always.
    check(
      'job_types_driver_comment_requirement_check',
      sql`driver_comment_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    check(
      'job_types_photo_requirement_check',
      sql`photo_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    check(
      'job_types_driver_time_requirement_check',
      sql`driver_time_requirement IN ('HIDDEN', 'OPTIONAL', 'REQUIRED')`,
    ),
    // D-03 — the partial CHECK: tank-only columns are NON-NULL on the TANK fork
    // and NULL on the LINE_ITEM fork. Makes the invalid half-configured state
    // unrepresentable instead of relying on application code.
    check(
      'job_types_line_item_tank_fields_null_check',
      sql`(ticket_shape = 'TANK'
             AND tank_count_mode IS NOT NULL
             AND water_volume_requirement IS NOT NULL
             AND oil_volume_requirement IS NOT NULL
             AND top_gauge_requirement IS NOT NULL
             AND bottom_gauge_requirement IS NOT NULL
             AND seal_requirement IS NOT NULL
             AND color_cut_requirement IS NOT NULL
             AND tank_name_requirement IS NOT NULL)
        OR (ticket_shape = 'LINE_ITEM'
             AND tank_count_mode IS NULL
             AND tank_count_max IS NULL
             AND water_volume_requirement IS NULL
             AND oil_volume_requirement IS NULL
             AND top_gauge_requirement IS NULL
             AND bottom_gauge_requirement IS NULL
             AND seal_requirement IS NULL
             AND color_cut_requirement IS NULL
             AND tank_name_requirement IS NULL)`,
    ),
    // DEC-025 rationale preserved under DEC-170's split: gauging an empty tank's
    // BOTTOM is meaningless without the full-tank TOP reading, so bottom may
    // never be stricter than top.
    check(
      'job_types_gauge_coherence_check',
      sql`ticket_shape <> 'TANK'
          OR (CASE top_gauge_requirement WHEN 'REQUIRED' THEN 3 WHEN 'OPTIONAL' THEN 2 ELSE 1 END)
             >= (CASE bottom_gauge_requirement WHEN 'REQUIRED' THEN 3 WHEN 'OPTIONAL' THEN 2 ELSE 1 END)`,
    ),
    // D-06 (Phase 12.1) — billing_method stays the SINGLE billing discriminator
    // (09.1's ticket_shape is orthogonal). A LINE_ITEM job type MUST bill
    // PER_UNIT: emit-billable-events.ts's per-line-item PER_UNIT emission loop
    // (Plan 12.1-03) runs UNCONDITIONALLY per captured line item, regardless of
    // billing_method — so a LINE_ITEM job type mis-set to HOURLY/PER_BBL/PER_LOAD
    // would let the existing per-BBL/HOURLY branches in that same function ALSO
    // fire (if the operator happens to have a per-BBL or hourly rate configured),
    // producing a genuine double-bill. This CHECK makes that misconfiguration
    // unrepresentable rather than relying on wizard-UI discipline alone (Plan
    // 12.1-04 additionally locks the wizard's billing-method field for LINE_ITEM
    // so a dispatcher never even sees the invalid choice).
    check(
      'job_types_line_item_billing_method_check',
      sql`ticket_shape <> 'LINE_ITEM' OR billing_method = 'PER_UNIT'`,
    ),
    // DEC-140 — tenant isolation (was a no-RLS platform catalog under D-16).
    pgPolicy('job_types_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type JobType = typeof jobTypes.$inferSelect;
export type NewJobType = typeof jobTypes.$inferInsert;
export const jobTypeSelectSchema = createSelectSchema(jobTypes);
export const jobTypeInsertSchema = createInsertSchema(jobTypes);

/**
 * job_type_customers — JOBTYPE-13 / legacy JobTypeCustomerXref parity.
 *
 * Scopes a job-type FAMILY (not a single version — scoping survives a version
 * fork) to a subset of the tenant's customers (`hauler_operators`).
 *
 * ALL-CUSTOMERS SENTINEL: ZERO rows for a family means the family is available
 * to EVERY customer. There is no magic UUID. Any picker query is therefore:
 *   family has no rows  OR  family has a row for the selected customer.
 */
export const jobTypeCustomers = pgTable(
  'job_type_customers',
  {
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    familyId: uuid('family_id').notNull(),
    haulerOperatorId: uuid('hauler_operator_id')
      .notNull()
      .references(() => haulerOperators.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    /**
     * Task 20.5's provenance marker, added late by migration 0118 (DEC-295).
     *
     * 0105 gave this to seventeen tables and skipped this one, reasoning that
     * T14 is UPDATE ONLY. That is true of its PRIMARY table and false of this
     * one: T14 declares `job_type_customers` as `INSERT_OR_UPDATE` and the
     * engine really does insert it, so the missing column was the FIRST error
     * on every T14 row carrying a "Job Types Enabled" cell. NULL means "not
     * created by an import", which is every row that existed before 0118.
     */
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    primaryKey({ columns: [t.tenantId, t.familyId, t.haulerOperatorId] }),
    index('job_type_customers_tenant_customer_idx').on(t.tenantId, t.haulerOperatorId),
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row is
    // import-created, so a full index would be mostly NULLs. Never backs an ON
    // CONFLICT (the writer targets the composite primary key above), so it does
    // not trip the partial-index/ON CONFLICT trap.
    index('job_type_customers_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // DEC-265 / 0107's composite shape, so a row cannot claim ANOTHER tenant's
    // batch. One detail Drizzle cannot express: the migration uses
    // ON DELETE SET NULL ("import_batch_id"), the Postgres 15 column-scoped
    // form, because a bare SET NULL would also null tenant_id (NOT NULL) and
    // make deleting a batch fail. 0118 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'job_type_customers_import_batch_tenant_fk',
    }).onDelete('set null'),
    pgPolicy('job_type_customers_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type JobTypeCustomer = typeof jobTypeCustomers.$inferSelect;
export type NewJobTypeCustomer = typeof jobTypeCustomers.$inferInsert;
export const jobTypeCustomerSelectSchema = createSelectSchema(jobTypeCustomers);
export const jobTypeCustomerInsertSchema = createInsertSchema(jobTypeCustomers);

// DEC-140 — `tenant_job_type_settings` (the D-06 overlay) was DROPPED (migration
// 0055). It was dormant (zero runtime reads/writes); its semantics are folded into
// the tenant-owned job_types columns (default_rate_cents / label / is_active /
// is_default_for_dispatch).

/**
 * jobs — central transactional table.
 *
 * RLS-enforced (T-02-02-01 HIGH). Six perf indexes from RESEARCH #6-11 cover
 * the dispatch queue, driver job-list, dispatcher dashboard, haul-id lookup,
 * site-history view, and "jobs I dispatched" panel.
 *
 * DEC-001 three-field DispatcherId split:
 *   - created_by_id (uuid, FK to users.id) — the user who created the job
 *   - created_by_type (text, CHECK allowlist) — the *category* of creator
 *     (DISPATCHER / DRIVER / FOREMAN / SYSTEM_AUTO_HAUL / SYSTEM_MQTT)
 *   - assigned_dispatcher_id (uuid, FK to users.id) — the dispatcher
 *     accountable for the job (may differ from created_by_id, e.g. a system
 *     auto-haul gets assigned to the on-call dispatcher)
 *
 * Master spec §14 + Phase 6 / Plan 06-01 / D-22 step 3 haul_id is varchar(20)
 * with CHECK regex `^[A-Z]{2,6}[0-9]{9}$` — `{abbrev}{9 digit sequence}`,
 * e.g. `RIV000000042`. Widened from the legacy varchar(9) digits-only column
 * via migration 0005. Nullable until generated by Phase 6 (the run-ticket
 * capture flow assigns it on first onsite arrival).
 *
 * DEFERRED FKs — see module docstring. site_id and operator_id are declared
 * as soft uuid columns; Plan 02-04 relations.ts adds the FK constraints
 * once Plan 02-03's company_sites and operators tables exist.
 */
export const jobs = pgTable(
  'jobs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // FK to job_statuses(id) — restrict on delete so a status with rows can't
    // be removed without first migrating the rows to another status.
    statusId: integer('status_id')
      .notNull()
      .references(() => jobStatuses.id, { onDelete: 'restrict' }),
    jobTypeId: integer('job_type_id')
      .notNull()
      .references(() => jobTypes.id, { onDelete: 'restrict' }),
    // Master spec §14 + Phase 6 / Plan 06-01 / D-22 step 3 — server-issued
    // canonical HaulId. Widened from varchar(9) (legacy 9-digit) to varchar(20)
    // to fit the new `{2-6-letter abbreviation}{9 digit sequence}` format
    // (e.g. `RIV000000042`, 3+9=12 chars). Nullable until the capture flow
    // assigns at first onsite arrival (master spec §14).
    haulId: varchar('haul_id', { length: 20 }),
    // DEC-001 three-field DispatcherId split.
    // Nullable since 0116 — but only for an OPERATOR_API job, which has no user, and since 0237
    // for a SYSTEM_AUTO_HAUL / SYSTEM_MQTT job (no person either). `jobs_created_by_check` keeps
    // it mandatory for every other created_by_type.
    createdById: uuid('created_by_id').references(() => users.id, { onDelete: 'restrict' }),
    // CHECK constraint defined below in the (t) => [...] callback.
    createdByType: text('created_by_type').notNull(),
    /**
     * 0116 / Task 18.6 — the operator API credential that created this job, when
     * `created_by_type = 'OPERATOR_API'` (and NULL otherwise — `jobs_created_by_check`).
     * Composite FK (tenant_id, this) → operator_api_credentials (tenant_id, id) in SQL,
     * so a job cannot name another hauler's key. Immutable after insert (trigger).
     */
    createdByApiCredentialId: uuid('created_by_api_credential_id'),
    /**
     * 0237 / Task 13.4b — the hauler tank an auto job (SYSTEM_AUTO_HAUL) was created for.
     * Composite FK (tenant_id, this) → hauler_tanks (tenant_id, id) in SQL, ON DELETE SET NULL.
     */
    autoHaulTankId: uuid('auto_haul_tank_id'),
    /**
     * 0237 — set by trigger jobs_auto_haul_open_set from the status: the auto job is not done yet.
     * A partial unique index allows one open auto job per tank. Never written by the API.
     */
    autoHaulOpen: boolean('auto_haul_open').notNull().default(false),
    /** 0237 — dispatch was told the tank was hauled another way while this auto job was open. */
    autoHaulBypassNotifiedAt: timestamp('auto_haul_bypass_notified_at', { withTimezone: true }),
    assignedDispatcherId: uuid('assigned_dispatcher_id').references(() => users.id, {
      onDelete: 'restrict',
    }),
    assignedDriverId: uuid('assigned_driver_id').references(() => users.id, {
      onDelete: 'restrict',
    }),
    // jobs.operator_id → operators.id (RESTRICT — preserve billing/audit trail;
    // billing still reads it). KEPT in Phase 26.2 — operators retirement is
    // billing-gated (DEC-147). Nullable: a PENDING job may have no operator yet.
    // (jobs.site_id → company_sites RETIRED — Phase 26.2 dropped the old model;
    //  pickup location is now jobs.hauler_location_id.)
    operatorId: uuid('operator_id').references(() => operators.id, { onDelete: 'restrict' }),
    // D-07 / Phase 06.1 — planned facility set at job creation; actual set when
    // driver taps Arrived at Disposal (defaults to planned; driver may override
    // via "Change destination" on DisposalScreen). Both ON DELETE RESTRICT to
    // preserve billing/compliance audit if a facility is soft-deleted.
    plannedDisposalFacilityId: uuid('planned_disposal_facility_id').references(
      () => disposalFacilities.id,
      { onDelete: 'restrict' },
    ),
    actualDisposalFacilityId: uuid('actual_disposal_facility_id').references(
      () => disposalFacilities.id,
      { onDelete: 'restrict' },
    ),
    // Phase 26 / DEC-147 #6 — generalized DROPOFF target. A dropoff-completed job
    // resolves to EITHER a disposal_facilities row (above) OR a dropoff-capable
    // hauler_locations row (below). At most one of each {planned,actual} pair is set
    // (DB CHECK jobs_{planned,actual}_dropoff_target_at_most_one); the app enforces
    // "exactly one on dropoff completion" (depends on job_types.completion_location,
    // not a jobs column). RESTRICT to preserve billing/compliance audit.
    plannedDropoffHaulerLocationId: uuid('planned_dropoff_hauler_location_id').references(
      () => haulerLocations.id,
      { onDelete: 'restrict' },
    ),
    actualDropoffHaulerLocationId: uuid('actual_dropoff_hauler_location_id').references(
      () => haulerLocations.id,
      { onDelete: 'restrict' },
    ),
    // 0208 (SWD destination A1, Corbin 2026-10-17) — the THIRD dropoff kind: a catalog
    // disposal well the hauler authorized (hauler_disposal_facilities). At most one of the
    // three planned (and of the three actual) targets is set.
    plannedDropoffDisposalWellId: uuid('planned_dropoff_disposal_well_id').references(
      () => haulerDisposalFacilities.id,
      { onDelete: 'restrict' },
    ),
    actualDropoffDisposalWellId: uuid('actual_dropoff_disposal_well_id').references(
      () => haulerDisposalFacilities.id,
      { onDelete: 'restrict' },
    ),
    scheduledAt: timestamp('scheduled_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    // Quick 260614-t4m (#6) — free-text dispatcher notes, edited from the web
    // Job Detail modal (PATCH /v1/jobs/:id/notes). Migration 0025.
    dispatcherNotes: text('dispatcher_notes'),
    // DEC-176 (ClickUp 86bb0yq4b, migration 0078) — job-level default product.
    // Set from the pinned job_type's default_product_id at creation; a dispatcher
    // may change it on an existing job. Flows down to PRE-FILL (not lock) each tank
    // capture's product; the driver can still override per-tank
    // (haul_capture_tanks.product_id). NEVER back-filled from tank captures.
    // ON DELETE SET NULL — products are normally soft-deleted; a hard delete just
    // drops the pre-fill pointer.
    productId: uuid('product_id').references(() => products.id, { onDelete: 'set null' }),
    // DEC-146 (Phase 17 urgent slice, migration 0072) — dispatcher/driver-set
    // urgency. Drives the amber URGENT badge + fires an immediate single-job
    // completion email (gated on operator_notification_settings.is_enabled).
    // Settable at creation, on any active job later, and by a driver on a
    // provisional ticket. `urgentNotifiedAt` is the send-once idempotency guard.
    isUrgent: boolean('is_urgent').notNull().default(false),
    urgentNotifiedAt: timestamp('urgent_notified_at', { withTimezone: true }),
    // (jobs.tank_id → site_tanks RETIRED — Phase 26.2 dropped the old model.)
    // Phase 24 / Task 24.4 (DEC-095) — new-model link to the hauler_operators
    // junction. Nullable: old-model jobs (operator_id/site_id) leave it NULL and
    // keep the legacy HaulId path. When set at job creation it drives the new
    // `{HAULER}-{OPERATOR}{9-digit}` HaulId generator. RESTRICT to preserve the
    // billing/audit trail (issued HaulIds are immutable, Task 24.4 highest-risk).
    haulerOperatorId: uuid('hauler_operator_id').references(() => haulerOperators.id, {
      onDelete: 'restrict',
    }),
    // Phase 24 / Task 24.10 (DEC-101) → Phase 26 (DEC-147, renamed from
    // tank_battery_id) — new-model PICKUP location link (the job's source
    // hauler_location). Nullable: old-model jobs keep site_id and leave this NULL.
    // When the dispatcher (or driver) creates a job from a location, this is set
    // alongside hauler_operator_id (resolved from the location) and activates the
    // mobile location geofence branch. RESTRICT so a location referenced by a job
    // can't be hard-deleted (deactivate instead). API contract still exposes this as
    // `tankBatteryId` until Task 26.5/27.
    haulerLocationId: uuid('hauler_location_id').references(() => haulerLocations.id, {
      onDelete: 'restrict',
    }),
    // Standalone-well pickup (quick 260717-kme). A well with a directly-attached
    // hauler_tank (the well_id-set / hauler_location_id-NULL XOR shape) can be a
    // pickup WITHOUT a tank battery. Mutually exclusive with hauler_location_id
    // (the jobs_pickup_location_xor_well CHECK) — a job's pickup is a battery OR
    // a well, never both; both-NULL is still allowed (operatorId-only / legacy).
    // The hauler_operator is resolved server-side from wells.operator_id → the
    // tenant's hauler_operators row; RESTRICT so a well referenced by a job
    // can't be hard-deleted.
    pickupWellId: uuid('pickup_well_id').references(() => wells.id, {
      onDelete: 'restrict',
    }),
    // #60 / DEC-365 (0188) — the well on the job's location the load actually came from.
    // Only for a location pickup; the API checks it is linked to that location
    // (hauler_location_wells is RLS-scoped, so not a DB CHECK). NULL = not recorded.
    sourceWellId: uuid('source_well_id').references(() => wells.id, {
      onDelete: 'restrict',
    }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // BILLCFG-04 / SC#19 / DEC-032 #5 — driver-entered PO / reference / order-number
    // captured on the run ticket. Nullable: only required when the parent operator has
    // `require_reference_id = true` — gate enforced by `POST /v1/jobs/:id/capture`
    // (Plan 06.2-16). Length is bounded at API Zod layer (max 64 chars), not at DB,
    // to keep migration cheap and format requirements easy to evolve. Added
    // 2026-05-19 per plan-check HIGH #1 resolution (no field existed to store this).
    referenceId: text('reference_id'),
    /**
     * 0116 / Task 18.6 (DEC-299) — the OPERATOR's own reference for a haul request made
     * through the public API, echoed on every API response. NOT `reference_id`: capture
     * overwrites that with whatever the driver types, so it cannot be echoed stably.
     * Stored VERBATIM — a deliberate DEC-147 exception, like `referenceId` at capture.
     * 1..64 chars (CHECK). Immutable after insert (trigger).
     */
    externalReference: text('external_reference'),
    /**
     * 0116 / Task 18.6 (DEC-299) — the operator's notes on an API haul request. Never
     * `dispatcher_notes`: an operator's words must not be mistaken for a dispatcher's.
     * Verbatim (DEC-147 notes exception). Immutable after insert (trigger).
     */
    operatorRequestNotes: text('operator_request_notes'),
    // Legacy "New Request" parity (ClickUp 86bb0…) — optional requester contact
    // fields: who asked for the haul + how to reach them. Free-text CONTACT info,
    // NOT hauler-entered identifiers, so exempt from the DEC-147 uppercase rule
    // (preserved as typed). Nullable; no DB format constraint — basic email/phone
    // sanity is enforced at the API Zod layer only. Migration 0073.
    requesterName: text('requester_name'),
    requesterPhone: text('requester_phone'),
    requesterEmail: text('requester_email'),
    /**
     * OPEN-053 / migration 0087 — WHICH requestor asked for this load, chosen by the
     * dispatcher from the two assigned to the pickup site/well. Single column: the ticket
     * asks for one checkbox ("which of the two requested the load"), not several.
     *
     * The three `requester_*` columns above stay, and are populated from this requestor at
     * write time as a point-in-time SNAPSHOT — correcting a phone number later must not
     * rewrite a ticket that has already been printed or invoiced. They also remain the
     * escape hatch for a site with no assignments, which is every site on day one.
     *
     * RESTRICT mirrors `haulerLocationId` below: a referenced catalog row is deactivated,
     * never deleted out from under historical tickets. Nullable so driver-created mobile
     * loads (out of scope for November) need no migration when they arrive.
     */
    requestorId: uuid('requestor_id').references(() => requestors.id, { onDelete: 'restrict' }),
    // DEC-031 #8 / JOBLIST-03 — dispatcher manual reorder; NULLS LAST sort
    // in the pending bucket of GET /v1/jobs. Phase 6.2 read-only; Phase 9
    // owns the drag-and-drop write UI.
    dispatcherSortOrder: integer('dispatcher_sort_order'),
    // #46 / DEC-361 (migration 0184) — when the reorder that last set this column's order
    // was made; the latest change wins by this time (services/job-order.ts).
    jobOrderSetAt: timestamp('job_order_set_at', { withTimezone: true }),
    // Phase 11 / Task 11.1 (DEC-120) — Truck Pusher InReview review layer. All
    // nullable; set by the auto-transition on completion (inreview_at +
    // inreview_flags) and by the later 11.2–11.6 review actions (reviewed_by,
    // reviewed_at, truck_pusher_notes). Never backfilled on pre-Phase-11
    // COMPLETED tickets — only new completions enter INREVIEW.
    inreviewAt: timestamp('inreview_at', { withTimezone: true }),
    // JSONB array of flag codes (GPS_OUTSIDE_RANGE, DISPATCHER_COMPLETED,
    // VOLUME_ANOMALY, MISSING_GAUGE, MISSING_SIGNATURE, DUPLICATE_TIMESTAMPS).
    inreviewFlags: jsonb('inreview_flags'),
    reviewedBy: uuid('reviewed_by').references(() => users.id, { onDelete: 'set null' }),
    reviewedAt: timestamp('reviewed_at', { withTimezone: true }),
    truckPusherNotes: text('truck_pusher_notes'),
    // ClickUp 86bamr1cz (DEC-122) — dispatcher job cancellation. All nullable;
    // set together by PATCH /v1/jobs/:id/cancel when a job is cancelled while in
    // PENDING (DISPATCH_MANAGER+) or EN_ROUTE_TO_PICKUP (TENANT_ADMIN, with an
    // explicit driver-en-route confirmation). ON DELETE SET NULL on cancelled_by
    // preserves the cancellation audit trail if the actor user is removed.
    cancelledAt: timestamp('cancelled_at', { withTimezone: true }),
    cancelledBy: uuid('cancelled_by').references(() => users.id, { onDelete: 'set null' }),
    cancellationReason: text('cancellation_reason'),
    // DEC-321 / OPEN-082 (migration 0128) — the CURRENT dispatcher "force to review" of a
    // stuck job with a captured load. All-or-none (except forced_review_by, which its FK may
    // NULL); "Return to stuck list" clears them, and the audit rows keep the history.
    // forced_review_clock_end_at is the driver's last real activity: hourly billing ends there
    // because a forced job never gets completed_at.
    forcedReviewAt: timestamp('forced_review_at', { withTimezone: true }),
    forcedReviewBy: uuid('forced_review_by').references(() => users.id, { onDelete: 'set null' }),
    forcedReviewReason: text('forced_review_reason'),
    forcedReviewPriorStatusId: integer('forced_review_prior_status_id').references(
      () => jobStatuses.id,
    ),
    forcedReviewClockEndAt: timestamp('forced_review_clock_end_at', { withTimezone: true }),
    // DEC-329 (migration 0154) — the manager who approved the forced review; NULL when the
    // company's policy required no approval (ALWAYS).
    forcedReviewApprovedBy: uuid('forced_review_approved_by').references(() => users.id, {
      onDelete: 'set null',
    }),
    // DEC-355 / OPEN-151 (migration 0177) — "Void load and cancel": the job's captured load
    // was a mistake. at + reason are set together (reason 10+ trimmed, DB CHECKs); every
    // operator-facing read skips a voided job. Nothing is deleted and the Haul ID is retired.
    captureVoidedAt: timestamp('capture_voided_at', { withTimezone: true }),
    captureVoidedBy: uuid('capture_voided_by').references(() => users.id, { onDelete: 'set null' }),
    captureVoidedReason: text('capture_voided_reason'),
    // "No disposal happened" (migration 0216, Corbin 2026-10-18) — the office's explicit record
    // that a disposal-ending load went to no disposal. at + reason are set together (reason 10+
    // trimmed, DB CHECK jobs_no_disposal_shape); while set there is no DISPOSAL_NOT_RECORDED and
    // no disposal fee. Recording an actual disposal clears all three.
    noDisposalAt: timestamp('no_disposal_at', { withTimezone: true }),
    noDisposalReason: text('no_disposal_reason'),
    noDisposalByUserId: uuid('no_disposal_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  (t) => [
    // RESEARCH perf index #6 — RLS lookup + dispatch queue filter.
    index('jobs_tenant_status_idx').on(t.tenantId, t.statusId),
    // RESEARCH perf index #7 — driver job queue.
    index('jobs_tenant_driver_status_idx').on(t.tenantId, t.assignedDriverId, t.statusId),
    // RESEARCH perf index #8 — dispatcher dashboard "newest pending jobs".
    index('jobs_tenant_status_created_idx').on(t.tenantId, t.statusId, t.createdAt.desc()),
    // RESEARCH perf index #9 — UNIQUE per-customer-sequence haul-id lookup
    // (widened from legacy 9-digit to `{abbrev}{9 digits}` via migration 0005).
    uniqueIndex('jobs_haul_id_idx').on(t.haulId),
    // (jobs_tenant_site_scheduled_idx on site_id RETIRED — Phase 26.2, old model dropped.)
    // Task 24.10 → Phase 26 — new-model pickup-location lookups (geofence activation join).
    index('jobs_tenant_hauler_location_idx').on(t.tenantId, t.haulerLocationId),
    // Standalone-well pickup lookups (quick 260717-kme).
    index('jobs_tenant_pickup_well_idx').on(t.tenantId, t.pickupWellId),
    // #60 / DEC-365 (0188) — source-well lookups, and the FK's RESTRICT check on a well delete.
    index('jobs_tenant_source_well_idx').on(t.tenantId, t.sourceWellId),
    // A job's pickup is a tank battery (hauler_location_id) XOR a standalone well
    // (pickup_well_id) — never both. Both-NULL allowed (operatorId-only jobs).
    check(
      'jobs_pickup_location_xor_well',
      sql`NOT (hauler_location_id IS NOT NULL AND pickup_well_id IS NOT NULL)`,
    ),
    // #60 / DEC-365 (0188) — a source well only refines a location pickup.
    check(
      'jobs_source_well_needs_location',
      sql`source_well_id IS NULL OR hauler_location_id IS NOT NULL`,
    ),
    // Phase 26 / DEC-147 #6 — dropoff-at-hauler-location lookups.
    index('jobs_tenant_actual_dropoff_location_idx').on(
      t.tenantId,
      t.actualDropoffHaulerLocationId,
    ),
    // 0208 — dropoff at an authorized disposal well.
    index('jobs_tenant_actual_dropoff_disposal_well_idx')
      .on(t.tenantId, t.actualDropoffDisposalWellId)
      .where(sql`actual_dropoff_disposal_well_id IS NOT NULL`),
    index('jobs_tenant_planned_dropoff_disposal_well_idx')
      .on(t.tenantId, t.plannedDropoffDisposalWellId)
      .where(sql`planned_dropoff_disposal_well_id IS NOT NULL`),
    // Phase 26 / DEC-147 #6, widened by 0208 — at most one dropoff target of the three kinds
    // (disposal facility, hauler location, disposal well). "Exactly one on dropoff
    // completion" is app-enforced.
    check(
      'jobs_planned_dropoff_target_at_most_one',
      sql`num_nonnulls(planned_disposal_facility_id, planned_dropoff_hauler_location_id, planned_dropoff_disposal_well_id) <= 1`,
    ),
    check(
      'jobs_actual_dropoff_target_at_most_one',
      sql`num_nonnulls(actual_disposal_facility_id, actual_dropoff_hauler_location_id, actual_dropoff_disposal_well_id) <= 1`,
    ),
    // RESEARCH perf index #11 — "jobs I dispatched today" dispatcher panel.
    index('jobs_tenant_dispatcher_created_idx').on(
      t.tenantId,
      t.assignedDispatcherId,
      t.createdAt.desc(),
    ),
    // DEC-001 — created_by_type allowlist; 'OPERATOR_API' added by 0116 (DEC-299).
    check(
      'jobs_created_by_type_check',
      sql`created_by_type IN ('DISPATCHER', 'DRIVER', 'FOREMAN', 'SYSTEM_AUTO_HAUL', 'SYSTEM_MQTT', 'OPERATOR_API')`,
    ),
    // 0116 — exactly one creator, and the type says which; 0237 — a system job has no person.
    check(
      'jobs_created_by_check',
      sql`(created_by_type = 'OPERATOR_API' AND created_by_api_credential_id IS NOT NULL AND created_by_id IS NULL) OR (created_by_type IN ('SYSTEM_AUTO_HAUL', 'SYSTEM_MQTT') AND created_by_api_credential_id IS NULL) OR (created_by_type NOT IN ('OPERATOR_API', 'SYSTEM_AUTO_HAUL', 'SYSTEM_MQTT') AND created_by_api_credential_id IS NULL AND created_by_id IS NOT NULL)`,
    ),
    // 0237 — only an auto job names a tank.
    check(
      'jobs_auto_haul_tank_check',
      sql`auto_haul_tank_id IS NULL OR created_by_type = 'SYSTEM_AUTO_HAUL'`,
    ),
    // 0237 — at most one open auto job per tank (auto_haul_open is kept by trigger).
    uniqueIndex('jobs_auto_haul_one_open_idx')
      .on(t.autoHaulTankId)
      .where(sql`auto_haul_open`),
    check(
      'jobs_external_reference_length_check',
      sql`external_reference IS NULL OR char_length(external_reference) BETWEEN 1 AND 64`,
    ),
    // Master spec §14 + Phase 6 / D-22 step 3 + Phase 24 / Task 24.4 (DEC-095).
    // NULL is allowed (assigned at job creation for new-model jobs, or on first
    // sync for driver provisional loads). When present it matches EITHER:
    //   - legacy `{2-6 letters}{9 digits}`              (e.g. `RIV000000042`), OR
    //   - new    `{2-4 letters}-{2-4 letters}{9 digits}` (e.g. `HAU-DVN000000001`).
    // Legacy ids stay valid and are NEVER rewritten (Task 24.4 highest-risk rule).
    check(
      'jobs_haul_id_format_check',
      sql`haul_id IS NULL OR haul_id ~ '^[A-Z]{2,6}[0-9]{9}$' OR haul_id ~ '^[A-Z]{2,4}-[A-Z]{2,4}[0-9]{9}$'`,
    ),
    // RLS — T-02-02-01 HIGH (the cornerstone of every Phase 5+ behavior).
    pgPolicy('jobs_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type Job = typeof jobs.$inferSelect;
export type NewJob = typeof jobs.$inferInsert;
export const jobSelectSchema = createSelectSchema(jobs);
export const jobInsertSchema = createInsertSchema(jobs);
